import type { Size } from '../geometry/framing.ts';
import type { QualityId } from './recipe.ts';

/** Bits per pixel per frame for H.264 at each quality level (empirical, social media grade). */
const BITS_PER_PIXEL: Readonly<Record<QualityId, number>> = { small: 0.05, balanced: 0.09, high: 0.15 };
const AUDIO_BITRATE = 128_000;

/** Frames a second as bitrate sees them: it grows sub-linearly above 30, since consecutive frames compress well. */
export function effectiveFrameRate(frameRate: number): number {
  const fps = Math.min(Math.max(frameRate || 30, 1), 120);
  return fps <= 30 ? fps : 30 + (fps - 30) * 0.5;
}

export function estimateVideoBitrate(size: Size, frameRate: number, quality: QualityId): number {
  return Math.round(size.width * size.height * effectiveFrameRate(frameRate) * BITS_PER_PIXEL[quality]);
}

/** Picture and sound together, in bits a second, as the export summary estimates them. */
export const estimateBitrate = (size: Size, frameRate: number, quality: QualityId, hasAudio: boolean): number =>
  estimateVideoBitrate(size, frameRate, quality) + (hasAudio ? AUDIO_BITRATE : 0);

/** Rough output size in bytes, used for the export summary. */
export function estimateFileSize(size: Size, frameRate: number, quality: QualityId, durationSec: number, hasAudio: boolean): number {
  return Math.round((estimateBitrate(size, frameRate, quality, hasAudio) * Math.max(0, durationSec)) / 8);
}
